import { ExternalLink, FileText, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/atoms/Button';

interface Props {
  /** Display name of the draft being opened (used in the modal body). */
  draftName: string;
  busy?: boolean;
  onCancel: () => void;
  /**
   * Open the draft in a brand-new Word window. Current document stays
   * untouched. Uses Word.Application.openDocument (WordApi 1.6) with a
   * fallback to createDocument(base64).open() (WordApi 1.3).
   */
  onOpenInNewWindow: () => void;
  /**
   * Replace the current document's content in place. Pushes a snapshot
   * for Revert. Universal fallback for Word builds without the
   * openDocument / createDocument APIs.
   */
  onLoadHere: () => void;
}

/**
 * Draft-switcher modal.
 *
 * Primary UX is opening in a new Word window (clean — current doc stays
 * as-is). "Load here" is retained as an explicit user choice for people
 * who want a single-window workflow, and as a fallback when the
 * openDocument API isn't available on the user's Word build.
 */
export function OpenDraftInNewWindowModal({
  draftName,
  busy,
  onCancel,
  onOpenInNewWindow,
  onLoadHere,
}: Props): JSX.Element {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-foreground/40 backdrop-blur-sm px-6">
      <div className="w-full max-w-sm rounded-lg bg-background border border-border shadow-lg p-4 space-y-3">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-foreground truncate" title={draftName}>
              {draftName || 'Untitled draft'}
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Open this draft in a new Word window (recommended) or load it into this
              window in place. Loading here replaces the current document&apos;s content,
              but your file on disk stays untouched until you save.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-1.5 pt-1">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadHere}
            disabled={busy}
            title="Replace the current document's content with the selected draft"
          >
            {busy ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            )}
            Load here
          </Button>
          <Button variant="default" size="sm" onClick={onOpenInNewWindow} disabled={busy}>
            {busy ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
            )}
            Open in new window
          </Button>
        </div>
      </div>
    </div>
  );
}
